import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import { If } from "components";
import { useBoolean } from "hooks";
import { t } from "i18next";
import moment from "moment";
import { Icon, Status, TableItemStatus } from "ui";

import {
  RESELLER_COMMISSION_STATUSES_LIST,
  ResellerCommissionStatus
} from "../../../constants";
import { formatAmountInFcfa } from "../../../helpers";
import { ResellerReferralOrder } from "../../../resellers.types";
import ReferralOrderProducts from "./ReferralOrderProducts";

import styles from "./ReferralOrders.module.scss";

interface ReferralOrderRowProps {
  order: ResellerReferralOrder;
  orderStatusesList: Status[];
  onOpenOrder: (orderId: string) => void;
}

/**
 * One order of a referree. Collapsed it answers "how much and did it pay";
 * expanded it lists the products, which is the level the client asked for.
 */
const ReferralOrderRow = ({
  order,
  orderStatusesList,
  onOpenOrder
}: ReferralOrderRowProps) => {
  const commissionStatusesList: Status[] =
    RESELLER_COMMISSION_STATUSES_LIST.map((status) => ({
      ...status,
      onClick: () => {}
    }));

  const isVoidCommission =
    order.commissionStatus === ResellerCommissionStatus.Void;

  // Own toggle rather than an accordion library: the products are fetched per
  // order, so the panel must not exist until the row is actually opened.
  const { value: isOpen, toggle: toggleOpen } = useBoolean(false);

  return (
    <div className={styles.orderRow}>
      <button
        type="button"
        className={styles.orderRow_button}
        onClick={toggleOpen}
      >
        <div className={styles.orderRow_summary}>
          <div className={styles.orderRow_id}>
            <span className={styles.orderRow_id_value}>{order.id}</span>
            <span className={styles.orderRow_id_date}>
              {moment(order.createdAt).format("DD/MM/YYYY HH:mm")}
            </span>
          </div>

          <TableItemStatus
            activeStatusIndex={orderStatusesList.findIndex(
              (status) => status.value === order.status
            )}
            statusesList={orderStatusesList}
          />

          <div className={styles.orderRow_cell}>
            <span className={styles.orderRow_label}>{t("Items")}</span>
            <span className={styles.orderRow_value}>{order.itemsCount}</span>
          </div>

          <div className={styles.orderRow_cell}>
            <span className={styles.orderRow_label}>{t("Total")}</span>
            <span className={styles.orderRow_value}>
              {formatAmountInFcfa(order.totalPrice)}
            </span>
          </div>

          <div className={styles.orderRow_cell}>
            <span className={styles.orderRow_label}>{t("Commission")}</span>
            <span
              className={cn(styles.orderRow_value, styles.orderRow_commission, {
                [styles.orderRow_commission__void]: isVoidCommission
              })}
            >
              {formatAmountInFcfa(order.commissionAmount)}
            </span>
            <TableItemStatus
              activeStatusIndex={commissionStatusesList.findIndex(
                (status) => status.value === order.commissionStatus
              )}
              statusesList={commissionStatusesList}
            />
          </div>

          <div
            className={cn(styles.orderRow_chevron, {
              [styles.orderRow_chevron__open]: isOpen
            })}
          >
            <Icon as={ArrowIcon} width={20} height={20} />
          </div>
        </div>
      </button>

      <If condition={isOpen}>
        <div className={styles.orderRow_panel}>
          <ReferralOrderProducts orderId={order.id} onOpenOrder={onOpenOrder} />
        </div>
      </If>
    </div>
  );
};

export default ReferralOrderRow;
